import React from "react";
import { Trash2 } from "lucide-react";
import { NonOrderProductCheckInColumnHeader } from "src/enums";
import { DateQtyInputField, NonOrderProductCheckInInputField, TableColumn } from "src/types";
import { DynamicDateQtyInputList } from "src/components";
import { TextInputBox } from "src/components/ui/inputs";
import { Table } from "src/components";

interface NonOrderProductCheckInInputTableProps {
  nonOrderProductCheckIns: NonOrderProductCheckInInputField[];
  setNonOrderProductCheckIns: React.Dispatch<React.SetStateAction<NonOrderProductCheckInInputField[]>>;
  minNonOrderProductCheckIns?: number;
}

const NonOrderProductCheckInInputTable: React.FC<NonOrderProductCheckInInputTableProps> = ({
  nonOrderProductCheckIns,
  setNonOrderProductCheckIns,
  minNonOrderProductCheckIns = 0
}) => {
  const handleProductCheckInsInputChange = <T,>(
    id: string,
    fieldName: keyof NonOrderProductCheckInInputField,
    value: T
  ) => {
    setNonOrderProductCheckIns(
      nonOrderProductCheckIns.map((productCheckIn) =>
        productCheckIn.fieldId === id ? { ...productCheckIn, [fieldName]: value } : productCheckIn
      )
    );
  };

  const handleSplitQtyChange = (id: string, fieldName: "receivedUkQty" | "receivedEuQty", value: string) => {
    setNonOrderProductCheckIns(
      nonOrderProductCheckIns.map((productCheckIn) => {
        if (productCheckIn.fieldId !== id) {
          return productCheckIn;
        }
        const updated = { ...productCheckIn, [fieldName]: value };
        const ukQty = Number(updated.receivedUkQty) || 0;
        const euQty = Number(updated.receivedEuQty) || 0;
        const total = ukQty + euQty;
        return { ...updated, receivedQty: total ? total.toString() : "" };
      })
    );
  };

  const showWarehouseBreakdown = nonOrderProductCheckIns.some((p) => p.hasWarehouseSplit);

  const NonOrderProductCheckInInputTableColumns: TableColumn<
    NonOrderProductCheckInColumnHeader,
    NonOrderProductCheckInInputField
  >[] = [
    {
      header: NonOrderProductCheckInColumnHeader.PRODUCT_ID,
      column: (row: NonOrderProductCheckInInputField) => (
        <div className="w-28">
          <TextInputBox
            id={`${row.fieldKey}-id-${row.fieldId}`}
            value={row.productId ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "productId", value)}
            maxLength={10}
            placeholder="ID (Optional)"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "120px" }
    },
    {
      header: NonOrderProductCheckInColumnHeader.PRODUCT_NAME,
      column: (row: NonOrderProductCheckInInputField) => (
        <div className="w-48">
          <TextInputBox
            id={`${row.fieldKey}-name-${row.fieldId}`}
            value={row.productName ?? ""}
            onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "productName", value)}
            maxLength={100}
            placeholder="Name"
            size="sm"
          />
        </div>
      ),
      styles: { minWidth: "200px" }
    },
    ...(showWarehouseBreakdown
      ? [
          {
            header: (
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs">Qty Received</span>
                <span className="text-[11px] font-normal tracking-wider opacity-70">(auto)</span>
              </div>
            ),
            column: (row: NonOrderProductCheckInInputField) => {
              const current = nonOrderProductCheckIns.find((p) => p.fieldId === row.fieldId);
              const total = (Number(current?.receivedUkQty) || 0) + (Number(current?.receivedEuQty) || 0);
              return (
                <div className="flex items-center justify-center">
                  <span className="font-semibold text-secondary-900 tabular-nums text-sm">{total}</span>
                </div>
              );
            },
            styles: { minWidth: "100px", textAlign: "center" as const }
          },
          {
            header: <span className="text-violet-300 text-xs font-semibold tracking-wider">UK Received</span>,
            column: (row: NonOrderProductCheckInInputField) => {
              if (!row.hasWarehouseSplit) {
                return <span className="text-sm text-gray-400">-</span>;
              }
              const value = (
                nonOrderProductCheckIns.find((p) => p.fieldId === row.fieldId)?.receivedUkQty ?? ""
              ).toString();
              return (
                <div className="w-24">
                  <TextInputBox
                    id={`${row.fieldKey}-received-uk-quantity-${row.fieldId}`}
                    value={value}
                    onChange={(val) => handleSplitQtyChange(row.fieldId, "receivedUkQty", val)}
                    maxLength={7}
                    placeholder="/ 0"
                    size="sm"
                  />
                </div>
              );
            },
            styles: { minWidth: "120px" }
          },
          {
            header: <span className="text-orange-300 text-xs font-semibold tracking-wider">EU Received</span>,
            column: (row: NonOrderProductCheckInInputField) => {
              if (!row.hasWarehouseSplit) {
                return <span className="text-sm text-gray-400">-</span>;
              }
              const value = (
                nonOrderProductCheckIns.find((p) => p.fieldId === row.fieldId)?.receivedEuQty ?? ""
              ).toString();
              return (
                <div className="w-24">
                  <TextInputBox
                    id={`${row.fieldKey}-received-eu-quantity-${row.fieldId}`}
                    value={value}
                    onChange={(val) => handleSplitQtyChange(row.fieldId, "receivedEuQty", val)}
                    maxLength={7}
                    placeholder="/ 0"
                    size="sm"
                  />
                </div>
              );
            },
            styles: { minWidth: "120px" }
          }
        ]
      : [
          {
            header: NonOrderProductCheckInColumnHeader.RECEIVED_QTY,
            column: (row: NonOrderProductCheckInInputField) => {
              const receivedQty =
                nonOrderProductCheckIns
                  .find((nonOrderProductCheckIn) => nonOrderProductCheckIn.fieldId === row.fieldId)
                  ?.receivedQty?.toString() || "";

              return (
                <div className="w-24">
                  <TextInputBox
                    id={`${row.fieldKey}-quantity-received-${row.fieldId}`}
                    value={receivedQty}
                    onChange={(value) => handleProductCheckInsInputChange(row.fieldId, "receivedQty", value)}
                    maxLength={40}
                    placeholder="/ 0"
                    size="sm"
                  />
                </div>
              );
            },
            styles: { minWidth: "120px" }
          }
        ]),
    {
      header: NonOrderProductCheckInColumnHeader.EXPIRY_DATES,
      column: (row: NonOrderProductCheckInInputField) => {
        const current = nonOrderProductCheckIns.find(
          (nonOrderProductCheckIn) => nonOrderProductCheckIn.fieldId === row.fieldId
        );
        const { expiryDates = [] } = current ?? {};
        const totalQty = current?.hasWarehouseSplit
          ? ((Number(current?.receivedUkQty) || 0) + (Number(current?.receivedEuQty) || 0)).toString()
          : (current?.receivedQty ?? "");
        return (
          <div>
            <DynamicDateQtyInputList
              id={`${row.fieldKey}-${row.fieldId}`}
              inputDates={expiryDates}
              onInputChange={(updatedExpiryDates: DateQtyInputField[]) => {
                handleProductCheckInsInputChange(row.fieldId, "expiryDates", updatedExpiryDates);
              }}
              totalQty={totalQty}
            />
          </div>
        );
      },
      styles: { minWidth: "200px" }
    },
    {
      header: NonOrderProductCheckInColumnHeader.PLACE_HOLDER,
      column: (row: NonOrderProductCheckInInputField) => (
        <button
          onClick={() => {
            if (nonOrderProductCheckIns.length > minNonOrderProductCheckIns) {
              setNonOrderProductCheckIns(
                nonOrderProductCheckIns.filter((productCheckIn) => productCheckIn.fieldId !== row.fieldId)
              );
            }
          }}
          disabled={nonOrderProductCheckIns.length <= minNonOrderProductCheckIns}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete extra product"
          type="button"
        >
          <Trash2 className="w-4 h-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return <Table columns={NonOrderProductCheckInInputTableColumns} data={nonOrderProductCheckIns} />;
};

export { NonOrderProductCheckInInputTable };
